import {
    ContactType as CONTACT_TYPE_VALUES,
    type ContactType,
    type PayIdType,
    PreferredContactMethod as PREFERRED_CONTACT_METHOD_VALUES,
    type PreferredContactMethod,
} from '@shared/prisma/enums';
import type { Decimal } from 'decimal.js';
import { DEFAULT_PAGE_SIZE } from '../system/general';

export const CONTACT_ENTITIES = {
    CLAIM: 'claim',
    SUPPLIER: 'supplier',
} as const;

export type ContactEntity = (typeof CONTACT_ENTITIES)[keyof typeof CONTACT_ENTITIES];

export { CONTACT_TYPE_VALUES as CONTACT_TYPES };
export const CORE_CONTACT_TYPES = [
    CONTACT_TYPE_VALUES.BROKER,
    CONTACT_TYPE_VALUES.SUPPLIER,
    CONTACT_TYPE_VALUES.INSURER,
    CONTACT_TYPE_VALUES.POLICY_HOLDER,
] as const satisfies ContactType[];

export const OTHER_CONTACT_TYPES = [
    CONTACT_TYPE_VALUES.THIRD_PARTY,
    CONTACT_TYPE_VALUES.DRIVER,
] as const satisfies Exclude<ContactType, (typeof CORE_CONTACT_TYPES)[number]>[];

export type OtherContactType = (typeof OTHER_CONTACT_TYPES)[number];

/**
 * Recipient types a questionnaire's lodgement confirmation can be sent to
 * (`Questionnaire.notificationSendTo`). Stored as the legacy lowercase contact-type
 * strings; the confirmation job upper-cases them to the Prisma `ContactType`. `driver`
 * targets the claim's primary DRIVER contact (CUR-985).
 */
export const QUESTIONNAIRE_NOTIFICATION_RECIPIENTS = [
    'policy_holder',
    'broker',
    'insurer',
    'supplier',
    'driver',
] as const satisfies Lowercase<ContactType>[];

export type QuestionnaireNotificationRecipient = (typeof QUESTIONNAIRE_NOTIFICATION_RECIPIENTS)[number];

/**
 * Non-disclosing denial messages the contact create/update procedures return for expected
 * business denials (closed parent record, missing write permission, anomalous contact state).
 *
 * Single source of truth: the server throws these verbatim from its contact route/service boundary and the client's Sentry
 * classifier (`client/src/trpc/error-classification.ts`) matches on them to suppress capture of
 * these expected denials. Rewording here updates both sides in one typechecked change.
 */
export const CONTACT_WRITE_DENIAL_MESSAGES = {
    CREATE: 'You are not able to create this contact.',
    UPDATE: 'You are not able to update this contact.',
} as const;

export const CONTACTS_WITH_IS_PRIMARY_FIELD = [
    CONTACT_TYPE_VALUES.BROKER,
    CONTACT_TYPE_VALUES.POLICY_HOLDER,
    CONTACT_TYPE_VALUES.THIRD_PARTY,
    CONTACT_TYPE_VALUES.DRIVER,
] as const satisfies ContactType[];

export type { PreferredContactMethod };
export { PREFERRED_CONTACT_METHOD_VALUES as PREFERRED_CONTACT_METHODS };

// Hand-rolled shape mirroring the browser-safe subset of the Prisma `Contact` model row
// (id, orgId, type, firstName, lastName, email, phone, isPrimaryForType, note, claimId, supplierId).
// Kept in sync with `server/prisma/models/Contact.prisma`.
export type BaseContact = {
    id: bigint;
    orgId: bigint;
    type: ContactType;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    isPrimaryForType: boolean;
    note: string | null;
    claimId: bigint | null;
    supplierId: bigint | null;
    entity: ContactEntity | null;
    entityId: bigint | null;
};

export type BrokerContact = BaseContact & {
    type: Extract<ContactType, 'BROKER'>;
    isPrimary: boolean;
    branch: string;
    ccIntoAllClaimCommunications: boolean;
};

export type SupplierContact = BaseContact & {
    type: Extract<ContactType, 'SUPPLIER'>;
    position: string;
};

export type InsurerContact = BaseContact & {
    type: Extract<ContactType, 'INSURER'>;
};

export type PolicyHolderContact = BaseContact & {
    type: Extract<ContactType, 'POLICY_HOLDER'>;
    isPrimary: boolean;
    birthDate: Date | null;
    addressLine1: string | null;
    addressLine2: string | null;
    addressSuburb: string | null;
    addressCity: string | null;
    addressState: string | null;
    addressPostcode: string | null;
    addressCountry: string | null;
    preferredContactMethod: PreferredContactMethod | null;
};

export type ThirdPartyContact = BaseContact & {
    type: Extract<ContactType, 'THIRD_PARTY'>;
    isPrimary: boolean;
    description: string | null;
    thirdPartyType: string;
    payIdType: PayIdType | null;
    payIdValue: string | null;
    accountBsb: string | null;
    accountName: string | null;
    accountNo: string | null;
    isRegisteredForGst: boolean | null;
    itcEntitlement: Decimal | null;
    abn: string | null;
};

export type DriverContact = BaseContact & {
    type: Extract<ContactType, 'DRIVER'>;
    isPrimary: boolean;
    description: string | null;
    birthDate: Date | null;
    addressLine1: string | null;
    addressLine2: string | null;
    addressSuburb: string | null;
    addressCity: string | null;
    addressState: string | null;
    addressPostcode: string | null;
    addressCountry: string | null;
    payIdType: PayIdType | null;
    payIdValue: string | null;
    accountBsb: string | null;
    accountName: string | null;
    accountNo: string | null;
    isRegisteredForGst: boolean | null;
    itcEntitlement: Decimal | null;
    abn: string | null;
    licenseNo: string | null;
    licenseExpiry: string | null;
};

export type Contact =
    | BrokerContact
    | SupplierContact
    | InsurerContact
    | PolicyHolderContact
    | ThirdPartyContact
    | DriverContact;

export type OtherContact = ThirdPartyContact | DriverContact;

export type ContactMap = {
    BROKER: BrokerContact;
    SUPPLIER: SupplierContact;
    INSURER: InsurerContact;
    POLICY_HOLDER: PolicyHolderContact;
    THIRD_PARTY: ThirdPartyContact;
    DRIVER: DriverContact;
};

export const CONTACT_TYPE_TO_TAG: Record<ContactType, string> = {
    BROKER: 'Broker',
    SUPPLIER: 'Supplier',
    INSURER: 'Insurer',
    POLICY_HOLDER: 'Policy holder',
    THIRD_PARTY: 'Third party',
    DRIVER: 'Driver',
} as const;

export const CONTACT_DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE;

/**
 * The one place that decides what a contact is *called*.
 *
 * `firstName`/`lastName` are non-null columns but may legitimately be empty strings: the
 * legacy lodgement path already writes `contact.firstName || ''`, and a product-version
 * contact type may collect only `COMPANY_NAME` (a repairer, a broking firm). Rendering
 * those as a blank name is the failure this resolver exists to prevent.
 *
 * Shared rather than duplicated per surface: the server (exports, comms recipients,
 * bordereau, AI context) and the client must never disagree about a contact's name.
 */
export function getContactDisplayName(contact: {
    id?: bigint | number | string | null;
    firstName?: string | null;
    lastName?: string | null;
    companyName?: string | null;
}): string {
    const personName = [contact.firstName, contact.lastName]
        .map((part) => part?.trim())
        .filter((part): part is string => !!part)
        .join(' ');

    if (personName) {
        return personName;
    }

    const companyName = contact.companyName?.trim();

    if (companyName) {
        return companyName;
    }

    return contact.id === undefined || contact.id === null ? 'Unnamed contact' : `Contact #${contact.id}`;
}

/**
 * The one primary-contact rule for product-version (v2) contacts: the first instance of a roled
 * contact type is the primary contact of that built-in type; unroled types never are. Applied by
 * the lodgement mapper and the product-contact editor, and relied on by everything that reads
 * `isPrimaryForType` (claim header, comms recipients, summaries, exports).
 */
export function isPrimaryContactInstance({
    systemType,
    instance,
}: {
    systemType: string | null;
    instance: number;
}): boolean {
    return systemType !== null && instance === 1;
}

/**
 * The contact's type label for lists, exports and AI context (CUR-1413): a products-v2 contact
 * carries it on its contact type (FK), a v1 third party in the legacy `thirdPartyType` string,
 * and every other contact in the built-in type's tag.
 */
export function getContactTypeLabel(contact: {
    type: ContactType;
    thirdPartyType?: string | null;
    ProductVersionContactType?: { name: string } | null;
}): string {
    const fk = contact.ProductVersionContactType?.name?.trim();
    if (fk) return fk;
    const legacy = contact.thirdPartyType?.trim();
    if (legacy) return legacy;
    return CONTACT_TYPE_TO_TAG[contact.type] ?? contact.type;
}
